import * as React from 'react';
import { Box, InputAdornment, Tooltip } from '@mui/material';
import { Icon } from '@mdi/react';
import { mdiCrown } from '@mdi/js';
import {
  AutocompleteInput,
  RaRecord,
  required,
  useChoicesContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';

type ProcessTemplateChoice = RaRecord & {
  processName?: string;
  xgacCustomerId?: string | null;
};

// Same rule as processTemplateTransforms: no customer id means global. The API may omit the key entirely.
export const isGlobalProcessTemplate = (record?: object | null) => (
  !!record && (record as { xgacCustomerId?: string | null }).xgacCustomerId == null
);

export const GlobalProcessTemplateIcon = () => {

  const translate = useTranslate();
  const label = translate('resources.process-templates.text.global_badge');

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        role="img"
        aria-label={label}
        sx={{
          display: 'inline-flex', flexShrink: 0, color: '#B8860B', lineHeight: 0,
        }}
      >
        <Icon path={mdiCrown} size="14px" />
      </Box>
    </Tooltip>
  );

};

export const ProcessTemplateName = ({ name, global }: { name?: string; global: boolean }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: '4px', verticalAlign: 'middle',
    }}
  >
    <span>{name}</span>
    {global ? <GlobalProcessTemplateIcon /> : null}
  </Box>
);

export const ProcessTemplateNameField = () => {

  const record = useRecordContext<ProcessTemplateChoice>();
  if (!record) return null;

  return (
    <ProcessTemplateName
      name={record.processName}
      global={isGlobalProcessTemplate(record)}
    />
  );

};

export const ProcessTemplateAutocompleteInput = () => {

  const { selectedChoices } = useChoicesContext<ProcessTemplateChoice>();
  const selected = selectedChoices?.[0];
  const showCrown = isGlobalProcessTemplate(selected);

  return (
    <AutocompleteInput
      validate={required()}
      autoHighlight
      filterToQuery={(searchText) => ({ search: searchText })}
      optionText={(choice: ProcessTemplateChoice) => (
        <ProcessTemplateName name={choice?.processName} global={isGlobalProcessTemplate(choice)} />
      )}
      inputText={(choice: ProcessTemplateChoice) => choice?.processName ?? ''}
      TextFieldProps={showCrown ? {
        InputProps: {
          startAdornment: (
            <InputAdornment position="start">
              <GlobalProcessTemplateIcon />
            </InputAdornment>
          ),
        },
      } : undefined}
    />
  );

};
